import React from 'react'
import { Bar } from 'react-chartjs-2'
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js'
import type { ChartData, ChartOptions } from 'chart.js'
import { Measurement } from '../../../../../../typings/reality-capture/volumes/models';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
)

// ColorLegend Component using Chart.js
export const ColorLegend = ({measurement}: {measurement: Measurement, height?: number}) => {
    const {lowest_point, highest_point, colors} = measurement;
    const lowestPoint = Number(lowest_point.toFixed(2));
    const highestPoint = Number(highest_point.toFixed(2));
    const isFlatRange = lowestPoint === highestPoint;
    const rangePadding = isFlatRange ? 0.01 : 0;
    const yMin = isFlatRange ? lowestPoint - rangePadding : lowestPoint;
    const yMax = isFlatRange ? highestPoint + rangePadding : highestPoint;

    function linearGradient(chart) {
        const {ctx: canvasCtx, chartArea} = chart;
        if (!chartArea) {
            return null;
        }

        const colorStops: [number, string][] = Array.isArray(colors) ? (colors as [number, string][]) : [];
        const gradient = canvasCtx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);

        colorStops.forEach(([offset, color]) => {
            gradient.addColorStop(offset, color);
        });
        return gradient;
    }
    
    function getBackground(ctx) {
        const chart = ctx.chart;
        return linearGradient(chart);
    }
    
    const chartData: ChartData<'bar', number[], string> = {
        labels: [''],
        datasets: [{
            data: [highestPoint],
            backgroundColor: (ctx) => {
                return getBackground(ctx)
            },
            borderWidth: 0,
            barThickness: 60,
            base: lowestPoint
        }]
    };

    const chartOptions: ChartOptions<'bar'> = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            title: {
                display: true,
                text: __('js_map_measuring_tools.color_legend'),
                color: 'black',
                font: { size: 14 },
                align: 'end' as const
            },
            legend: {
                display: false
            },
            tooltip: {
                enabled: false
            }
        },
        scales: {
            x: {
                display: false,
                grid: {
                    display: false
                }
            },
            y: {
                display: true,
                grid: {
                    display: true
                },
                ticks: {
                    callback: (value) => {
                        const num = typeof value === 'number' ? value : Number(value);
                        return `${Number.isFinite(num) ? num.toFixed(2) : value} m`;
                    },
                },
                min: yMin,
                max: yMax,
            }
        },
        layout: {
            padding: {
                left: 0,
                right: 8,
                bottom: 0,
                top: 0
            }
        }
    };

    return (
        <div style={{ width: '150px', height: '100%', minHeight: '80px' }}>
            <Bar className="w-full h-full" data={chartData} options={chartOptions} />
        </div>
    )
};
